import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NavBar } from '../../components/NavBar';
import { api } from '../../lib/api';
import type { Home } from '@home-guard/shared';

const FUEL_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  propane:      { label: 'Propane',      bg: 'bg-amber-100',  text: 'text-amber-800'  },
  electric:     { label: 'Electric',     bg: 'bg-blue-100',   text: 'text-blue-800'   },
  natural_gas:  { label: 'Natural Gas',  bg: 'bg-orange-100', text: 'text-orange-800' },
  oil:          { label: 'Oil',          bg: 'bg-gray-100',   text: 'text-gray-700'   },
  mixed:        { label: 'Mixed',        bg: 'bg-purple-100', text: 'text-purple-800' },
};

const CLIMATE_TYPE_LABEL: Record<string, string> = {
  tropical:          'Tropical',
  hot_humid:         'Hot Humid',
  hot_dry:           'Hot Dry',
  mixed_humid:       'Mixed Humid',
  cold_continental:  'Cold Continental',
  very_cold:         'Very Cold',
  pacific_maritime:  'Pacific Maritime',
  mountain:          'Mountain',
};

const FREEZE_RISK_BADGE: Record<string, { bg: string; text: string }> = {
  low:      { bg: 'bg-sky-50',     text: 'text-sky-700'    },
  moderate: { bg: 'bg-blue-100',   text: 'text-blue-800'   },
  high:     { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  extreme:  { bg: 'bg-violet-100', text: 'text-violet-900' },
};

function formatClimateType(t: string): string {
  return CLIMATE_TYPE_LABEL[t] ?? t.replace(/_/g, ' ');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function scoreLabel(n: number): string {
  if (n >= 90) return 'Excellent';
  if (n >= 70) return 'Good';
  if (n >= 50) return 'Fair';
  return 'Needs attention';
}

function SmallRing({ score }: { score: number }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <svg width="96" height="96" viewBox="0 0 96 96">
      <circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="8" />
      <circle cx="48" cy="48" r={r} fill="none" stroke="#fff" strokeWidth="8"
        strokeDasharray={c} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 48 48)" />
      <text x="48" y="45" textAnchor="middle" fontSize="22" fontWeight="700" fill="#fff">{score}</text>
      <text x="48" y="61" textAnchor="middle" fontSize="10" fill="rgba(255,255,255,0.75)">/ 100</text>
    </svg>
  );
}

function DetailBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl p-3 flex flex-col items-center gap-0.5">
      <span className="text-lg font-bold text-gray-900">{value}</span>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 capitalize">{value.replace(/_/g, ' ')}</span>
    </div>
  );
}

function LinkRow({ label, to, count }: { label: string; to: string; count?: number }) {
  return (
    <Link to={to} className="flex items-center justify-between py-3.5 border-b border-gray-100 last:border-0 no-underline">
      <span className="text-sm font-medium text-gray-900">{label}</span>
      <div className="flex items-center gap-2">
        {count !== undefined && (
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{count}</span>
        )}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
    </Link>
  );
}

export function HomeProfile() {
  const navigate = useNavigate();

  const { data: homesData, isLoading } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });

  const { data: appliancesData } = useQuery({
    queryKey: ['appliances', (homesData?.data ?? []).find(h => h.is_primary)?.id ?? ''],
    queryFn: () => {
      const home = (homesData?.data ?? []).find(h => h.is_primary) ?? homesData?.data?.[0];
      return home ? api.get<{ data: unknown[] }>(`/homes/${home.id}/appliances`) : Promise.resolve({ data: [] });
    },
    enabled: !!homesData?.data?.length,
  });

  const homes = homesData?.data ?? [];
  const home = homes.find(h => h.is_primary) ?? homes[0];

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!home) {
    navigate('/app/home/setup', { replace: true });
    return null;
  }

  const fuelCfg = home.fuel_type ? (FUEL_CONFIG[home.fuel_type] ?? null) : null;
  const applianceCount = appliancesData?.data?.length ?? 0;

  return (
    <div className="min-h-dvh bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-700 to-blue-900 pt-12 pb-6 px-4">
        <div className="flex items-start justify-between mb-4">
          <button
            onClick={() => navigate('/app/dashboard')}
            className="flex items-center gap-1 text-white/80 text-sm font-medium"
            style={{ minHeight: 44 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Dashboard
          </button>
          <Link
            to="/app/profile/edit"
            className="text-white/90 text-sm font-semibold bg-white/20 px-3 py-1.5 rounded-full"
            style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}
          >
            Edit
          </Link>
        </div>

        <div className="flex items-center gap-4">
          <SmallRing score={home.health_score} />
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-base leading-tight truncate">{home.address_line1}</p>
            <p className="text-white/70 text-sm mt-0.5">
              {home.city}, {home.state} {home.zip_code}
            </p>
            <p className="text-white/60 text-xs mt-1">{scoreLabel(home.health_score)}</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">
        {/* Stats grid */}
        <div className="grid grid-cols-4 gap-2">
          <DetailBox label="Built" value={home.year_built ? String(home.year_built) : '—'} />
          <DetailBox label="Sq ft" value={home.sq_footage ? home.sq_footage.toLocaleString() : '—'} />
          <DetailBox label="Beds" value={home.bedrooms !== null ? String(home.bedrooms) : '—'} />
          <DetailBox label="Baths" value={home.bathrooms !== null ? String(home.bathrooms) : '—'} />
        </div>

        {/* Attributes card */}
        <div className="bg-white rounded-2xl shadow-sm px-4">
          <InfoRow label="Structure" value={home.structure_type} />
          <InfoRow label="Roof" value={home.roof_type} />
          <InfoRow label="Foundation" value={home.foundation_type} />
          {home.climate_zone && <InfoRow label="Climate zone" value={home.climate_zone} />}
          <InfoRow label="Pipe material" value={home.pipe_material} />
          {home.pipe_age_years !== null && (
            <InfoRow label="Pipe age" value={`${home.pipe_age_years} years`} />
          )}
          <InfoRow label="Window type" value={home.window_type} />
          <InfoRow label="Window frames" value={home.window_material} />
          <InfoRow label="Driveway" value={home.driveway_material} />
          {home.has_garage && home.garage_door_count !== null && (
            <InfoRow label="Garage doors" value={String(home.garage_door_count)} />
          )}
          {home.has_deck && <InfoRow label="Deck material" value={home.deck_material ?? 'Yes'} />}
          {home.has_fence && <InfoRow label="Fence material" value={home.fence_material ?? 'Yes'} />}
          {home.has_ev_charger && (
            <InfoRow label="EV charger" value={home.ev_charger_level ?? 'Yes'} />
          )}
        </div>

        {/* Badges row */}
        <div className="flex gap-2 flex-wrap">
          {fuelCfg && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${fuelCfg.bg} ${fuelCfg.text}`}>
              {fuelCfg.label}
            </span>
          )}
          {home.fuel_type === 'propane' && home.propane_tank_size && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">
              {home.propane_tank_size} gal tank · {home.propane_tank_owned ? 'Owned' : 'Leased'}
            </span>
          )}
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${home.has_solar ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
            {home.has_solar ? 'Solar active' : 'No solar'}
          </span>
          {home.is_primary && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-blue-50 text-blue-700">
              Primary home
            </span>
          )}
          {/* Climate intelligence badges */}
          {home.hardiness_zone && home.climate_type && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-teal-100 text-teal-800">
              Zone {home.hardiness_zone} · {formatClimateType(home.climate_type)}
            </span>
          )}
          {home.freeze_risk && home.freeze_risk !== 'none' && (() => {
            const cfg = FREEZE_RISK_BADGE[home.freeze_risk] ?? { bg: 'bg-gray-100', text: 'text-gray-700' };
            return (
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                {capitalize(home.freeze_risk)} freeze risk
              </span>
            );
          })()}
          {home.hurricane_risk && home.hurricane_risk !== 'none' && (
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-orange-100 text-orange-800">
              Hurricane {home.hurricane_risk} risk
            </span>
          )}
          {home.has_well && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-sky-100 text-sky-800">
              Well water
            </span>
          )}
          {home.has_septic && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-lime-100 text-lime-800">
              Septic system
            </span>
          )}
          {home.has_sump_pump && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-blue-50 text-blue-700">
              Sump pump
            </span>
          )}
          {home.has_garage && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
              Garage
            </span>
          )}
          {home.has_deck && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">
              Deck
            </span>
          )}
          {home.has_fence && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-stone-100 text-stone-700">
              Fence
            </span>
          )}
          {home.has_smart_home && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-violet-100 text-violet-800">
              Smart home
            </span>
          )}
          {home.has_ev_charger && (
            <span className="text-xs font-medium px-3 py-1.5 rounded-full bg-green-100 text-green-800">
              EV charger
            </span>
          )}
        </div>

        {/* Navigation links */}
        <div className="bg-white rounded-2xl shadow-sm px-4">
          <LinkRow label="Appliances" to="/app/appliances" count={applianceCount} />
          <LinkRow label="Paint Colors" to="/app/paint-colors" />
          {home.has_solar
            ? <LinkRow label="Solar System" to="/app/solar" />
            : (
              <Link to="/app/solar" className="flex items-center justify-between py-3.5 no-underline">
                <span className="text-sm font-medium text-gray-400">Add Solar System</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </Link>
            )
          }
        </div>
      </div>

      <NavBar />
    </div>
  );
}
